
// Available languages for the legacy nav/header translation layer.
// This stays narrow on purpose — react-i18next is the long-term home for
// translations. We only widen this when a locale wants the header/nav to feel
// native too (which today means: en, es, pt-BR, fr).
export type Language = 'en' | 'es' | 'pt-BR' | 'fr';

// Core translations used throughout the app
export type CoreTranslations = {
  navigation: {
    home: string;
    dashboard: string;
    teams: string;
    createPoll: string;
    stats: string;
    settings: string;
    login: string;
    logout: string;
    parent: string;
    docs: string;
  };
  userMenu: {
    profile: string;
    settings: string;
    logout: string;
    viewingAs: string;
    parent: string;
  };
  modeToggle: {
    parentMode: string;
    coachMode: string;
    switchToParent: string;
    switchToCoach: string;
  };
  toasts: {
    signedOut: string;
    signOutFailed: string;
  };
  settings: {
    title: string;
    language: string;
    languageLabel: string;
    theme: string;
    themeLabel: string;
    saveChanges: string;
    savedSuccessfully: string;
  };
};

// Translation data by language
export const translations: Record<Language, CoreTranslations> = {
  en: {
    navigation: {
      home: 'Home',
      dashboard: 'Dashboard',
      teams: 'Teams',
      createPoll: 'New Match',
      stats: 'Stats',
      settings: 'Settings',
      login: 'Login',
      logout: 'Logout',
      parent: 'My Kids',
      docs: 'Docs',
    },
    userMenu: {
      profile: 'Profile',
      settings: 'Settings',
      logout: 'Logout',
      viewingAs: 'Viewing as',
      parent: 'Parent',
    },
    modeToggle: {
      parentMode: 'Parent mode',
      coachMode: '{role} mode',
      switchToParent: 'Switch to Parent mode',
      switchToCoach: 'Switch to {role} mode',
    },
    toasts: {
      signedOut: 'Signed out successfully',
      signOutFailed: 'Failed to sign out. Please try again.',
    },
    settings: {
      title: 'Settings',
      language: 'Language',
      languageLabel: 'Select language',
      theme: 'Theme',
      themeLabel: 'Select theme',
      saveChanges: 'Save Changes',
      savedSuccessfully: 'Settings saved successfully',
    },
  },
  es: {
    navigation: {
      home: 'Inicio',
      dashboard: 'Panel',
      teams: 'Equipos',
      createPoll: 'Nuevo partido',
      stats: 'Estadísticas',
      settings: 'Ajustes',
      login: 'Iniciar sesión',
      logout: 'Cerrar sesión',
      parent: 'Mis hijos',
      docs: 'Documentos',
    },
    userMenu: {
      profile: 'Perfil',
      settings: 'Ajustes',
      logout: 'Cerrar sesión',
      viewingAs: 'Viendo como',
      parent: 'Familia',
    },
    modeToggle: {
      parentMode: 'Modo familia',
      coachMode: 'Modo {role}',
      switchToParent: 'Cambiar a modo familia',
      switchToCoach: 'Cambiar a modo {role}',
    },
    toasts: {
      signedOut: 'Sesión cerrada',
      signOutFailed: 'No pudimos cerrar la sesión. Inténtalo de nuevo.',
    },
    settings: {
      title: 'Ajustes',
      language: 'Idioma',
      languageLabel: 'Seleccionar idioma',
      theme: 'Tema',
      themeLabel: 'Seleccionar tema',
      saveChanges: 'Guardar cambios',
      savedSuccessfully: 'Ajustes guardados con éxito',
    },
  },
  'pt-BR': {
    navigation: {
      home: 'Início',
      dashboard: 'Painel',
      teams: 'Times',
      createPoll: 'Nova partida',
      stats: 'Estatísticas',
      settings: 'Configurações',
      login: 'Entrar',
      logout: 'Sair',
      parent: 'Meus filhos',
      docs: 'Documentos',
    },
    userMenu: {
      profile: 'Perfil',
      settings: 'Configurações',
      logout: 'Sair',
      viewingAs: 'Visualizando como',
      parent: 'Família',
    },
    modeToggle: {
      parentMode: 'Modo família',
      coachMode: 'Modo {role}',
      switchToParent: 'Mudar para modo família',
      switchToCoach: 'Mudar para modo {role}',
    },
    toasts: {
      signedOut: 'Sessão encerrada',
      signOutFailed: 'Não conseguimos encerrar a sessão. Tente de novo.',
    },
    settings: {
      title: 'Configurações',
      language: 'Idioma',
      languageLabel: 'Selecionar idioma',
      theme: 'Tema',
      themeLabel: 'Selecionar tema',
      saveChanges: 'Salvar alterações',
      savedSuccessfully: 'Configurações salvas com sucesso',
    },
  },
  fr: {
    navigation: {
      home: 'Accueil',
      dashboard: 'Tableau de bord',
      teams: 'Équipes',
      createPoll: 'Nouveau match',
      stats: 'Statistiques',
      settings: 'Paramètres',
      login: 'Se connecter',
      logout: 'Se déconnecter',
      parent: 'Mes enfants',
      docs: 'Documents',
    },
    userMenu: {
      profile: 'Profil',
      settings: 'Paramètres',
      logout: 'Se déconnecter',
      viewingAs: 'Vue active',
      parent: 'Famille',
    },
    modeToggle: {
      parentMode: 'Mode famille',
      coachMode: 'Mode {role}',
      switchToParent: 'Passer en mode famille',
      switchToCoach: 'Passer en mode {role}',
    },
    toasts: {
      signedOut: 'Vous êtes déconnecté·e',
      signOutFailed: 'Impossible de vous déconnecter. Réessayez.',
    },
    settings: {
      title: 'Paramètres',
      language: 'Langue',
      languageLabel: 'Sélectionner la langue',
      theme: 'Thème',
      themeLabel: 'Sélectionner le thème',
      saveChanges: 'Enregistrer les modifications',
      savedSuccessfully: 'Paramètres enregistrés',
    },
  },
};

// Default language
export const DEFAULT_LANGUAGE: Language = 'en';

/**
 * Resolve any locale tag (BCP-47 like "es-MX", "pt-BR", "qps-ploc") to the
 * narrowest supported nav locale. Pseudo-locale falls back to English so QA
 * can still navigate; unknown locales also fall back.
 */
export function resolveNavLanguage(input: string | null | undefined): Language {
  if (!input) return DEFAULT_LANGUAGE;
  if (input in translations) return input as Language;
  // pt-* → pt-BR (Brazilian is the only Portuguese we ship), es-* → es, fr-* → fr
  const base = input.split('-')[0].toLowerCase();
  if (base === 'pt') return 'pt-BR';
  if (base === 'es') return 'es';
  if (base === 'fr') return 'fr';
  return DEFAULT_LANGUAGE;
}

// Get translation string with optional named placeholder substitution: {role}, {name}, …
export function getTranslation(
  lang: Language,
  path: string,
  vars?: Record<string, string | number>,
): string {
  const keys = path.split('.');
  let result: any = translations[lang];
  
  for (const key of keys) {
    if (result?.[key] === undefined) {
      console.warn(`Missing translation: ${path} for language ${lang}`);
      
      // Fallback to English
      if (lang !== DEFAULT_LANGUAGE) {
        return getTranslation(DEFAULT_LANGUAGE, path, vars);
      }
      
      return path;
    }
    
    result = result[key];
  }
  
  if (typeof result !== 'string') return path;
  if (!vars) return result;
  return result.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

// Get user initials from email
export function getUserInitials(email: string | null): string {
  if (!email) return 'U';
  
  const parts = email.split('@');
  if (!parts[0]) return 'U';
  
  // Get first character of name part
  const firstChar = parts[0].charAt(0).toUpperCase();
  
  // Try to get last part of name if it contains dots or underscores
  let secondChar = '';
  if (parts[0].includes('.') || parts[0].includes('_') || parts[0].includes('-')) {
    const nameParts = parts[0].split(/[._-]/);
    if (nameParts.length > 1 && nameParts[nameParts.length - 1]) {
      secondChar = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    }
  }
  
  return firstChar + (secondChar || '');
}
